import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { pick } from 'lodash';
import { Marker } from 'mapbox-gl';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead/public_api';
import { ToastrService } from 'ngx-toastr';
import * as turf from '@turf/turf';
import { AuthService } from '../../shared/auth.service';
import { dateAsString, utcDatePartsToLocalDate } from '../../shared/date.helper';
import { Inspection, Inspector } from '../../shared/interfaces';
import { UserService } from '../../user/user.service';
import { VendorService } from '../../vendor/vendor.service';
import { InspectionService } from '../inspection.service';
import { InspectionStatus } from '../inspection.status';
import { InspectorService } from '../../inspector/inspector.service';
import { MapboxComponent } from '../../shared/mapbox/mapbox.component';
import { DialogService } from '../../dialog/dialog.service';

@Component({
  selector: 'app-inspection-vendor-form',
  templateUrl: './inspection-vendor-form.component.html',
  styleUrls: ['./inspection-vendor-form.component.scss'],
})
export class InspectionVendorFormComponent implements OnInit {
  // make available to html
  dateAsString = dateAsString;
  statusAsText: InspectionService['statusAsText'];
  InspectionStatus = InspectionStatus;

  dateConfig = { dateInputFormat: 'MM-DD-YYYY', customTodayClass: 'date-today' };

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public inspectionService: InspectionService,
    public vendorService: VendorService,
    public authService: AuthService,
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    public inspectorService: InspectorService,
    private dialog: DialogService,
    private userService: UserService,
  ) {
    this.statusAsText = inspectionService.statusAsText.bind(inspectionService);
  }

  inspection = {} as Inspection;
  form: UntypedFormGroup;

  @ViewChild('mapbox') mapboxComponent: MapboxComponent;
  @ViewChild('inspectionNotes') public inspectionNotes;
  map: MapboxComponent;
  lastLocation: [number, number];

  inspectorDataSource: any[] = [];
  selectedVendor: any;
  selectedInspector: any;
  showSaveButton: boolean = true;
  isInspectorsLoaded = false;
  inspectorMismatch: boolean = false;
  viewMoreClicked: boolean = false;
  inspectionProductTypeName: string;
  allInspectors: any[] = [];
  vendorInspectors: any[] = [];

  // Notes
  showModal = false;

  ngOnInit() {
    this.form = this.fb.group({
      flags: this.fb.group({
        completed: [{ value: null, disabled: true }],
        rejected: [{ value: null, disabled: true }],
        resubmitted: [{ value: null, disabled: true }],
        accepted: [{ value: null, disabled: true }],
        paid: [{ value: null, disabled: true }],
        status: [{ value: null, disabled: true }],
      }),
      customer: this.fb.group({
        productType: [{ value: '', disabled: true }],
        policyNumber: [{ value: '', disabled: true }],
        policyEffectiveDate: [{ value: '', disabled: true }],
        policyGroup: [{ value: '', disabled: true }],
        customerName: [{ value: '', disabled: true }],
        contactName: [{ value: '', disabled: true }],
        contactEmail: [{ value: '', disabled: true }],
        contactPhone: [{ value: '', disabled: true }],
        brokerName: [{ value: '', disabled: true }],
        brokerEmail: [{ value: '', disabled: true }],
        brokerPhone: [{ value: '', disabled: true }],
      }),
      locations: this.fb.group({
        locationId: [{ value: '', disabled: true }],
        address: [{ value: '', disabled: true }],
        city: [{ value: '', disabled: true }],
        state: [{ value: '', disabled: true }],
        zip: [{ value: '', disabled: true }],
        lat: [{ value: null, disabled: true }],
        lng: [{ value: null, disabled: true }],
        dueDate: [{ value: '', disabled: true }],
        forecasted: [{ value: false, disabled: true }],
        notes: [{ value: '', disabled: true }],
        items: this.fb.array([], []),
      }),
      vendor: this.fb.group({
        vendor: [{ value: '', disabled: true }],
        inspector: [''],
      }),
      version: [{ value: null, disabled: true }],
      purpose: [{ value: '', disabled: true }],
    });

    this.route.data.subscribe((data: { inspection: Inspection }) => {
      if (data.inspection) {
        this.inspection = data.inspection;
        this.setData(data.inspection);
      }
    });
  }

  typeaheadOnSelectInspector(ev: TypeaheadMatch) {
    this.selectedInspector = ev.item;
  }

  userBlurInspector() {
    if (this.form.get('vendor.inspector').value === '') {
      this.selectedInspector = null;
    } else {
      this.form.get('vendor.inspector').setValue(this.selectedInspector ? this.selectedInspector.name : '');
    }
  }

  async setData(data: Inspection) {
    // Disable fields
    switch (data.status) {
      case InspectionStatus.ACCEPTED:
        this.form.get('vendor.inspector').disable();
        this.showSaveButton = false;
        break;
      case InspectionStatus.DEPRECATED_COMPLETED:
        this.form.get('vendor.inspector').disable();
        this.showSaveButton = false;
        break;
      case InspectionStatus.NEEDS_REVIEW:
        this.form.get('vendor.inspector').disable();
        this.showSaveButton = false;
        break;
      case InspectionStatus.INVALID_STATE:
        this.form.get('locations.dueDate').disable();
        this.form.get('vendor.inspector').disable();
        break;

      case InspectionStatus.REJECTED:
        this.form.get('locations.dueDate').disable();
        this.form.get('vendor.inspector').disable();
        this.form.get('vendor.inspector').setValidators([Validators.required]);
        this.showSaveButton = false;
        break;

      case InspectionStatus.ASSIGNMENT_MADE:
        this.form.get('locations.dueDate').disable();
        this.form.get('locations.dueDate').setValidators([Validators.required]);
        break;
    }

    // Set values
    this.form.patchValue({
      flags: {
        completed: data.completed ? new Date(data.completed) : null,
        rejected: data.rejected ? new Date(data.rejected) : null,
        resubmitted: data.resubmitted ? new Date(data.resubmitted) : null,
        accepted: data.accepted ? new Date(data.accepted) : null,
        paid: data.paid ? new Date(data.paid) : null,
        status: this.statusAsText(data),
      },
      customer: {
        ...pick(data, 'customerName', 'contactName', 'contactEmail', 'contactPhone'),
        policyNumber: data.policy?.number || data.policyNumber || '',
        policyEffectiveDate: utcDatePartsToLocalDate(data.policyEffectiveDate),
        productType: data?.location?.policy?.productType?.name || '',
        policyGroup: data?.location?.policy?.group?.name || '',
        brokerName: data?.location?.policy?.broker?.name || '',
        brokerEmail: data?.location?.policy?.broker?.email || '',
        brokerPhone: data?.location?.policy?.broker?.phone || '',
      },
      locations: {
        ...pick(
          data,
          'address',
          'city',
          'state',
          'zip',
          'dueDate',
          'forecasted',
          'locationId',
          'numberPoultryHouses',
          'numberGeneratorBuildings',
          'numberLitterSheds',
          'numberDwellings',
          'notes',
        ),
        dueDate: utcDatePartsToLocalDate(data.dueDate),
        forecasted: data.forecasted || false,
        lat: data.coordinates && data.coordinates[1],
        lng: data.coordinates && data.coordinates[0],
      },
      vendor: {
        vendor: data.vendor ? data.vendor.name : '',
        inspector: data.inspector ? data.inspector.name : '',
      },
      version: data.version || null,
      purpose: data.purpose || '',
    });

    await this.setupItems();

    this.selectedVendor = data.vendor;
    this.selectedInspector = data.inspector;

    this.inspectionProductTypeName = data?.location?.policy?.productType?.name;
    this.loadVendorInspectors();

    if (
      this.selectedInspector &&
      !this.vendorInspectors.some(inspector => inspector.id === this.selectedInspector.id)
    ) {
      this.vendorInspectors.push(this.selectedInspector);
      this.inspectorDataSource = [...this.vendorInspectors];
    }

    this.form.markAsUntouched();
    this.form.markAsPristine();

    // Update map marker after data is loaded
    if (data.coordinates && Array.isArray(data.coordinates)) {
      setTimeout(() => this.setMarker([data.coordinates[0], data.coordinates[1]]), 100);
    }
  }

  async submit() {
    const { customer, locations, vendor, flags } = this.form.controls;

    const controls = Object.assign(
      {},
      (customer as any).controls,
      (locations as any).controls,
      (vendor as any).controls,
      (flags as any).controls,
    );

    const value = {} as any;

    for (const c of Object.keys(controls)) {
      controls[c].markAsTouched();
      value[c] = controls[c].value;
    }

    if (!this.form.valid) {
      return;
    }

    const inspectionDTO = {
      ...value,
      dueDate: value.dueDate ? new Date(value.dueDate) : null,
      policyEffectiveDate: value.policyEffectiveDate ? new Date(value.policyEffectiveDate) : null,
      vendor: this.selectedVendor,
      inspector: this.selectedInspector,
    };

    if (this.inspection.id) {
      try {
        this.inspection = await this.inspectionService.edit(this.inspection.id, inspectionDTO);
        this.setData(this.inspection);
        this.toastr.success('Inspection saved!');
      } catch (e) {
        console.log('error edit', e);
      }
    } else {
      try {
        const inspection = await this.inspectionService.create(inspectionDTO);
        this.router.navigate(['/inspection/form', inspection.id]);
        this.toastr.success('Inspection saved!');
      } catch (e) {
        console.log('error create', e);
      }
    }
  }

  getValidationClass(group: string, field: string) {
    const control = this.form.get([group, field]);
    return {
      'is-valid': control.valid && (control.dirty || control.touched),
      'is-invalid': control.invalid && (control.dirty || control.touched),
    };
  }

  async setupItems() {
    const formArray = this.form.get('locations.items') as UntypedFormArray;

    formArray.clear();
    // First load, set quantities from inspection

    this.inspection.items.forEach(info => {
      const quantity = this.fb.control(info.quantity);
      quantity.disable();

      const customNames = this.fb.control(info.names && info.names.length);

      const newInfo = this.fb.control({
        ...info,
        quantity,
        customNames,
      });

      this.setItemNames(newInfo);

      formArray.push(newInfo);
    });
  }

  setItemNames(infoControl) {
    const info = infoControl.value;

    const oldInputs = info.namesInputs;
    info.namesInputs = [];
    const quantity = info.quantity.value;

    // application Crash check : if user enter more maxInstances
    if (quantity <= info.item?.maxInstances) {
      for (let i = BigInt(0); i < quantity; i++) {
        const index = Number(i);

        if (oldInputs && oldInputs[index]) {
          info.namesInputs.push(oldInputs[index]);
        } else {
          const control = this.fb.control((info.names && info.names[index]) || '', []);
          (control as any).itemName = info.item.name;
          control.disable();
          info.namesInputs.push(control);
        }
      }
    }
  }

  onInspectorSelect(inspector: any) {
    if (!inspector) {
      this.selectedInspector = null;
      this.form.get('vendor.inspector').setValue('');
      this.refreshInspectorList();
      return;
    }

    this.selectedInspector = inspector;
    this.selectInspector(inspector);

    if (!this.inspectorHasMatchingProductType(inspector)) {
      this.inspectorMismatch = true;
    } else {
      this.inspectorMismatch = false;
    }
  }

  refreshInspectorList() {
    if (!this.inspectionProductTypeName) {
      return;
    }

    this.vendorInspectors = this.allInspectors.filter(inspector => {
      if (!inspector.productTypesConcat || typeof inspector.productTypesConcat !== 'string') {
        return false;
      }
      const productTypesArray = inspector.productTypesConcat.split(',').map(pt => pt.trim());
      return productTypesArray.includes(this.inspectionProductTypeName);
    });

    this.inspectorDataSource = this.vendorInspectors;
  }

  inspectorHasMatchingProductType(inspector: any): boolean {
    if (!inspector.productTypesConcat || typeof inspector.productTypesConcat !== 'string') {
      return false;
    }
    const productTypesArray = inspector.productTypesConcat.split(',').map(pt => pt.trim());
    return productTypesArray.includes(this.inspectionProductTypeName);
  }

  selectInspector(inspector: any) {
    this.selectedInspector = inspector;
    this.cdr.detectChanges();
  }

  loadAllInspectors() {
    this.inspectorService
      .inspectorsWithMiles({
        search: '',
        distanceTo: [this.inspection.coordinates[0], this.inspection.coordinates[1]].toString(),
      })
      .pipe()
      .subscribe(
        inspectors => {
          this.inspectorDataSource = Array.isArray(inspectors) ? [...inspectors] : [{}];
          this.isInspectorsLoaded = true;
          this.cdr.markForCheck();
        },
        error => {
          console.error('Error fetching inspectors:', error);
          this.inspectorDataSource = [];
        },
      );
  }

  inspectorTrackByFn(inspector: Inspector) {
    return inspector.id;
  }

  viewMoreInspectors() {
    this.viewMoreClicked = true;
    this.loadAllInspectors();
  }

  handleDropdownClose() {
    this.viewMoreClicked = false;
  }

  async loadVendorInspectors() {
    this.inspectorMismatch = false;
    this.allInspectors = (await this.inspectorService
      .inspectorsWithMiles({
        search: '',
        distanceTo: [this.inspection.coordinates[0], this.inspection.coordinates[1]].toString(),
      })
      .toPromise()) || [{}];

    const inspectionProductTypeName = this.inspectionProductTypeName;

    if (!inspectionProductTypeName) {
      return;
    }

    this.vendorInspectors = this.allInspectors.filter(inspector => {
      if (!inspector.productTypesConcat || typeof inspector.productTypesConcat !== 'string') {
        return false;
      }
      const productTypesArray = inspector.productTypesConcat.split(',').map(pt => pt.trim());
      return productTypesArray.includes(inspectionProductTypeName);
    });

    if (this.selectedInspector) {
      const fullInspector = this.allInspectors.find(i => i.id === this.selectedInspector.id);

      if (!this.vendorInspectors.some(i => i.id === this.selectedInspector.id)) {
        this.vendorInspectors.push(fullInspector || this.selectedInspector);
      }
    }

    this.inspectorDataSource = this.vendorInspectors;
  }

  async clearInspector() {
    this.viewMoreClicked = false;
    await this.loadVendorInspectors();
    this.selectedInspector = null;
    this.form.get('vendor.inspector').setValue('');
  }

  setMap(mapboxComponent: MapboxComponent) {
    this.map = mapboxComponent;
    // Ensure map is properly sized before setting markers
    setTimeout(() => {
      this.map.map.resize();
      this.updateMapAfterSetting();
    }, 100);
  }

  updateMapAfterSetting() {
    const locations = this.form.get('locations').value;
    if (locations.lat || locations.lng) {
      this.updateMapFromForm();
    }
  }

  styleLoaded() {
    if (!this.map.map.getLayer('circleLayer') && this.lastLocation) {
      this.setMarker();
    }
  }

  setMarker(center?: [number, number]) {
    if (!this.map) {
      return;
    }
    try {
      this.lastLocation = center || this.lastLocation;
      const marker = this.createMarker(this.lastLocation);
      this.addMarkerToMap(marker);
      this.addCircleToMap(this.lastLocation);
    } catch (e) {}
  }

  createMarker(location: [number, number]) {
    const marker = new Marker().setLngLat(location);
    return marker;
  }

  addMarkerToMap(marker: Marker) {
    this.map.removeMarkers();
    this.map.addMarker(marker);
    // Resize the map to ensure proper dimensions before centering
    this.map.map.resize();
    this.map.jumpTo({
      zoom: 14,
      center: this.lastLocation,
    });
  }

  addCircleToMap(center: [number, number]) {
    // remove old layer
    if (this.map.map.getLayer('circleLayer')) {
      this.map.map.removeLayer('circleLayer');
      this.map.map.removeSource('circleSource');
    }
    const circle = turf.circle(center, 0.5, { steps: 64, units: 'miles' });
    this.map.map.addSource('circleSource', { type: 'geojson', data: circle });
    this.map.map.addLayer({
      id: 'circleLayer',
      type: 'fill',
      source: 'circleSource',
      paint: {
        'fill-color': 'white',
        'fill-opacity': 0.3,
      },
    });
  }

  updateMapFromForm() {
    const lat = this.form.get('locations.lat')?.value;
    const lng = this.form.get('locations.lng')?.value;

    if (lat && lng) {
      this.setMarker([lng, lat]);
    }
  }

  showActionButtons(): boolean {
    const status = this.statusAsText(this.inspection);
    return (
      !this.inspection.inspector || status === 'Assignment made' || status === 'Due completion' || status === 'Rejected'
    );
  }

  scrollToAssignments() {
    const element = document.getElementById('assignments-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  daysSince(date: string): number {
    if (!date) return 0;
    return Math.round((Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24));
  }

  // Send reminder functionality
  sendReminderDue() {
    this.dialog.confirm().show({
      message: 'Send reminder to Inspector?',
      confirmed: async () => {
        await this.inspectionService.sendReminderToInspectorDue(this.inspection.id);
        this.toastr.success('Message sent!');
      },
    });
  }

  sendReminderRejected() {
    this.dialog.confirm().show({
      message: 'Send reminder to Inspector?',
      confirmed: async () => {
        await this.inspectionService.sendReminderToInspectorRejected(this.inspection.id);
        this.toastr.success('Message sent!');
      },
    });
  }

  async createReport() {
    const currentUser = await this.userService.profile();
    const currentUserId = currentUser?.id;
    const list = await this.inspectionService.listReports(this.inspection.id);
    const running = list.find(
      r => !r.completed && r.requestedByUserId != null && r.requestedByUserId === currentUserId,
    );
    if (running) {
      this.dialog.alert().show({
        header: `Report generation is in progress`,
        message: `A report for this inspection is already being generated. You'll receive a download link via email once it's ready.`,
      });
      return;
    }
    this.dialog.confirm().show({
      header: 'Create inspection report',
      message: `The report will be generated and you'll receive a download link in your e-mail when it's ready`,
      confirmed: async () => {
        this.toastr.success('Your report is being generated');
        const reportParams = {
          applyFilters: false,
          showAdminComments: false,
          showFlags: false,
          showInspectorComments: true,
          showLegacyIds: false,
          showMarkupOverPictures: false,
          showNonMandatory: true,
          showQuestions: true,
        };
        await this.inspectionService.generateReport(this.inspection.id, {
          ...reportParams,
          filters: undefined,
        });
      },
    });
  }

  // Notes functionality
  showAddNoteModal(): void {
    this.showModal = true;
    this.cdr.detectChanges();
  }

  hideAddNoteModal(): void {
    this.showModal = false;
    this.cdr.detectChanges();
  }

  onNoteCreated(createdNote: any): void {
    this.toastr.success('Note saved successfully!');
    if (this.inspectionNotes) {
      this.inspectionNotes.addNewNote(createdNote);
    }
    this.hideAddNoteModal();
  }

  handleNoteClicked() {
    // Scroll to notes section in left panel if needed
  }
}
